import { message } from 'antd'
import { useEffect, useState } from 'react'
interface UseImageUploadProps<TImage> {
  images: TImage[]
  uploadApi: (fd: FormData) => Promise<any>
  deleteApi: (image: TImage, index: number) => Promise<any>
  onImagesChange?: (images: TImage[]) => void
}

interface UseImageUploadReturn<TImage> {
  images: TImage[]
  selectedImageIndex: number
  isUploading: boolean
  setSelectedImageIndex: (index: number) => void
  handleImageClick: (index: number) => void
  uploadImage: (file: File) => Promise<void>
  deleteImage: (index?: number) => Promise<void>
  resetImageSelection: () => void
}

export function useImageUpload<TImage>({
  images: externalImages,
  uploadApi,
  deleteApi,
  onImagesChange,
}: UseImageUploadProps<TImage>): UseImageUploadReturn<TImage> {
  const [images, setImages] = useState<TImage[]>(externalImages || [])
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    setImages(externalImages || [])
  }, [externalImages])

  const resetImageSelection = () => {
    setSelectedImageIndex(0)
  }

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
  }

  const uploadImage = async (file: File) => {
    if (!file.type.includes('image') && file.type !== 'application/pdf') {
      message.error('Please upload a valid image file')
      return
    }
    try {
      setIsUploading(true)
      const formData = new FormData()
      formData.append('file', file)
      const response = await uploadApi(formData)
      const newImage = response?.data

      if (!newImage?.url) {
        throw new Error('Uploaded image does not contain a valid URL')
      }

      const updatedImages = [...images, newImage]
      setImages(updatedImages)
      onImagesChange?.(updatedImages)
      setSelectedImageIndex(updatedImages.length - 1)
    } catch (error) {
      console.error('Error uploading image:', error)
    } finally {
      setIsUploading(false)
    }
  }

  const deleteImage = async () => {
    const imageToDelete = images[selectedImageIndex]

    if (!imageToDelete) {
      console.warn('No image found at index:', selectedImageIndex)
      return
    }

    try {
      await deleteApi(imageToDelete, selectedImageIndex)
      const updatedImages = images.filter((_, i) => i !== selectedImageIndex)

      if (updatedImages.length > 0) {
        setSelectedImageIndex(Math.min(selectedImageIndex, updatedImages.length - 1))
      } else {
        resetImageSelection()
      }
      setImages(updatedImages)
      onImagesChange?.(updatedImages)
    } catch (error) {
      console.error('Error deleting image:', error)
    }
  }

  return {
    images,
    selectedImageIndex,
    isUploading,
    setSelectedImageIndex,
    handleImageClick,
    uploadImage,
    deleteImage,
    resetImageSelection,
  }
}
